"use client";

import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { Link } from "@commerce-frontend/ui/base/Link/Link";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from "@commerce-frontend/ui/components/Dialog/Dialog";
import { useWindowWidth } from "@commerce-frontend/ui/hooks/responsive";
import { isDealerCustomerGroupKey } from "@commerce-shared/site-config";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTranslations } from "next-intl";
import { memo, useMemo } from "react";
import { LogoutButton } from "~/components/LogoutButton/LogoutButton";
import { useUserData } from "~/hooks/auth";
import { useCustomer } from "~/hooks/customer";
import { useStaticUrl } from "~/hooks/static-url";
import { DealerAccountNameIntegration } from "../../../account/_components/DealerAccountName.integration";
import type { AccountMenuItem } from "../../../account/_components/layout/types";
import { AccountMenuItems } from "../AccountMenuItems/AccountMenuItems";
import TopBar from "../MobileMainMenu/TopBar";

type Props = {
	accountLinks: AccountMenuItem[];
};

export const AccountLink = memo(({ accountLinks }: Props) => {
	const t = useTranslations("AccountLink");
	const loginUrl = useStaticUrl("login");
	const userData = useUserData();
	const { windowWidth } = useWindowWidth();
	const { customer } = useCustomer();

	const isDesktop = useMemo(
		() => windowWidth.atLeastDesktop,
		[windowWidth.atLeastDesktop],
	);
	const isTabletLandscape = useMemo(
		() => windowWidth.atLeastTabletLandscape,
		[windowWidth.atLeastTabletLandscape],
	);
	const isDealer = useMemo(
		() => isDealerCustomerGroupKey(customer?.customerGroup?.key),
		[customer?.customerGroup?.key],
	);
	const firstName = useMemo(
		() => customer?.firstName || userData?.firstName || t("nameFallback"),
		[customer, userData, t],
	);

	return userData ? (
		<>
			{isDesktop ? (
				<DropdownMenu.Root>
					<DropdownMenu.Trigger
						aria-label={t("accessibilityAccountLink")}
						className="flex cursor-pointer items-center gap-x-2 rounded-full p-2 transition-colors hover:bg-blue-400 hover:text-white blacknovember:hover:bg-black blacknovember:hover:opacity-80 max-md:-m-2 md:px-4"
					>
						<Icon icon="user" className="bg-current" />
						<span className="w-max font-bold">{firstName}</span>
					</DropdownMenu.Trigger>
					<DropdownMenu.Content className="relative top-5 z-10 w-60 divide-y rounded bg-background shadow-object">
						{isDealer && (
							<div className="p-4">
								<DealerAccountNameIntegration />
							</div>
						)}
						{accountLinks.map((item) => (
							<AccountLinkItem key={item.href}>
								<Link
									href={item.href}
									className="flex items-center gap-2 p-4 text-sm"
								>
									<Icon icon={item.icon} className="size-6 bg-main" />
									{item.label}
								</Link>
							</AccountLinkItem>
						))}
						<AccountLinkItem>
							<LogoutButton className="flex w-full items-center gap-2 rounded-none p-4 text-sm">
								<Icon icon="logout" className="size-6 text-main" />
								{t("logOut")}
							</LogoutButton>
						</AccountLinkItem>
					</DropdownMenu.Content>
				</DropdownMenu.Root>
			) : (
				<Dialog>
					<DialogTrigger
						aria-label={t("accessibilityAccountLink")}
						className="flex cursor-pointer items-center gap-x-2 rounded-full p-2 transition-colors hover:bg-blue-400 max-md:-m-2"
					>
						<Icon icon="user-logged-in" className="bg-current" />
					</DialogTrigger>
					<DialogContent side={isTabletLandscape ? "right" : "bottom"}>
						<DialogTitle className="sr-only">{t("dialogTitle")}</DialogTitle>

						{isDealer ? (
							<TopBar>
								<div className="p-4">
									<DealerAccountNameIntegration />
								</div>
							</TopBar>
						) : (
							<TopBar title={t("dialogTitle")} />
						)}
						<nav className="px-4">
							<AccountMenuItems items={accountLinks} />
						</nav>
					</DialogContent>
				</Dialog>
			)}
		</>
	) : (
		<Link
			className="flex items-center gap-x-2 rounded-full p-2 transition-colors hover:bg-blue-400 hover:text-white blacknovember:hover:bg-black blacknovember:hover:opacity-80 max-md:-m-2"
			href={loginUrl}
			aria-label={t("account")}
		>
			<Icon icon="user" className="bg-current" />
		</Link>
	);
});

AccountLink.displayName = "AccountLink";

const AccountLinkItem = memo(({ children }: { children: React.ReactNode }) => (
	<DropdownMenu.Item
		className="hover:ring-opacity-/0 text-blue-300 hover:outline-none hover:ring-0 hover:ring-offset-0 data-[highlighted]:text-blue-500"
		asChild
	>
		{children}
	</DropdownMenu.Item>
));

AccountLinkItem.displayName = "AccountLinkItem";
